import { useNavigate } from 'react-router-dom';
import { countLimit } from '../../../../utils/globalUtils';
import { FEATURE_KEYS, getFeatureApis } from '../../../../config/featureRegistry';
import useToast from '../../../../hooks/useToast';
import analytics from '../../../../utils/analytics';
import { dashboardCardConstants, ROUTES_PATH } from '../../../../constants/globalConstant';
import { useMobileTeamsData } from '../../mobile/MobileTeamsDataContext';

/**
 * Card-action handlers for the Mobile Teams screen's homepage cards.
 *
 * Mirrors the web pattern: looks up the Homepage feature config and calls
 * its API surface directly (same endpoints the feature-route listing uses
 * via useFeature()). MobileTeams is not a feature route so useFeature()
 * can't be used here — getFeatureApis is the equivalent escape hatch.
 *
 * Listing data (homepagesById) and team refetch come from
 * MobileTeamsDataContext, which the MobileTeams screen populates.
 */
function useHomepageCardRegistry() {
  const { homepagesById, refetchHomepages, refetchTeamsV2 } = useMobileTeamsData();
  const navigate = useNavigate();
  const toast = useToast();

  const homepageFeatureApis = getFeatureApis(FEATURE_KEYS.HOMEPAGE)?.apis;

  const sendEvent = (name, category, homepage) => {
    analytics.sendEvent(name, {
      category,
      type: 'click',
      homepageId: homepage?.homepageId,
      status: homepage?.status,
      homepageName: homepage?.homepageName,
      tab: 'Teams View'
    });
  };

  const cloneHomepageClicked = async (homepage, sourceClicked = '') => {
    const homepageId = homepage?.homepageId;
    if (Object.keys(homepagesById).length >= countLimit.HOMEPAGES_COUNT) {
      toast.warn(`Maximum ${countLimit.HOMEPAGES_COUNT} homepages can be added`);
      return;
    }
    const response = await homepageFeatureApis.clone(homepageId);
    if (response?.status === 200) {
      navigate(ROUTES_PATH.homebuilder);
    }
    analytics.sendEvent(`clonenhomepage${sourceClicked && ' clicked from ' + sourceClicked}`, {
      homepageId: homepage.homepageId,
      status: homepage.status,
      homepageName: homepage.homepageName
    });
  };

  const createHomepageClicked = (sourceClicked = '') => {
    if (Object.keys(homepagesById).length < countLimit.HOMEPAGES_COUNT) {
      // Navigate to builder without ID - BuilderDataContext will auto-create
      navigate(ROUTES_PATH.homebuilder);

      analytics.sendEvent(
        `create new homepage ${sourceClicked && ' clicked from ' + sourceClicked}`,
        { category: 'create_new_homepage', type: 'click' }
      );
    } else {
      toast.warn(`Maximum ${countLimit.HOMEPAGES_COUNT} homepages can be added`);
    }
  };

  const deleteHomepageClicked = async (homepage, sourceClicked = '') => {
    const homepageId = homepage.homepageId;
    const response = await homepageFeatureApis.delete(homepageId);
    if (response?.status === 200) {
      toast.success(`${homepage.homepageName} was deleted successfully!`);
      refetchHomepages();
      refetchTeamsV2();
    }
    sendEvent(
      `delete homepage ${sourceClicked && ' clicked from ' + sourceClicked}`,
      'delete_homepage',
      homepage
    );
  };

  const unpublishHomepageClicked = async (homepage, sourceClicked = '') => {
    const homepageId = homepage.homepageId;
    const response = await homepageFeatureApis.unpublish(homepageId);
    if (response?.status === 200) {
      toast.success(
        `${homepage.homepageName} was successfully ${dashboardCardConstants.UNPUBLISHED}!`
      );
      refetchHomepages();
      refetchTeamsV2();
    }
    sendEvent(
      `unpublish homepage ${sourceClicked && ' clicked from ' + sourceClicked}`,
      'unpublish_homepage',
      homepage
    );
  };

  const publishHomepageClicked = async (homepage, sourceClicked = '') => {
    const homepageId = homepage.homepageId;
    const postBody = {
      status: dashboardCardConstants.PUBLISHED
    };
    const response = await homepageFeatureApis.publish(homepageId, postBody);
    if (response?.status === 200) {
      toast.success(`${homepage.homepageName} was successfully published!`);
      refetchHomepages();
      refetchTeamsV2();
    }
    sendEvent(
      `publish homepage ${sourceClicked && ' clicked from ' + sourceClicked}`,
      'publish_homepage',
      homepage
    );
  };

  const unassignHompeageClicked = async (homepage, teamId, sourceClicked = '') => {
    const homepageId = homepage.homepageId;
    const isDefault = homepage.isDefault;

    const postBody = {
      ...(isDefault === true && { isDefault: false }),
      addTeams: [],
      deleteTeams: isDefault ? [] : [teamId]
    };

    const response = await homepageFeatureApis.updateTeams(homepageId, postBody);
    if (response?.status === 200) {
      toast.success(`${homepage.homepageName} was unassigned from the team!`);
      refetchHomepages();
      refetchTeamsV2();
    }
    sendEvent(
      `unassign homepage ${sourceClicked && ' clicked from ' + sourceClicked}`,
      'unassign_homepage',
      homepage
    );
  };

  return {
    cloneHomepageClicked,
    createHomepageClicked,
    deleteHomepageClicked,
    unpublishHomepageClicked,
    publishHomepageClicked,
    unassignHompeageClicked
  };
}

export default useHomepageCardRegistry;
